<template>
  <div class="WDropdownMenu">
    <DropdownMenuRoot v-model:open="open">
      <DropdownMenuTrigger class="DropdownMenuTrigger" :data-test="dataTest">
        <slot name="trigger" class="menu-trigger">
          <!-- default trigger when no slot was set-->
          <div class="default-trigger">
            <iconify-icon icon="material-symbols:more-vert" />
          </div>
        </slot>
      </DropdownMenuTrigger>

      <DropdownMenuPortal>
        <DropdownMenuContent :align="props.align" class="DropdownContent" :class="{ center: center }">
          <slot name="content">
            <span class="small">No options</span>
          </slot>
        </DropdownMenuContent>
      </DropdownMenuPortal>
    </DropdownMenuRoot>
  </div>
</template>

<script setup lang="ts">
  import { DropdownMenuContent, DropdownMenuPortal, DropdownMenuRoot, DropdownMenuTrigger } from 'radix-vue';
  import { ref, watch } from 'vue';

  const props = withDefaults(
    defineProps<{
      dataTest?: string;
      center?: boolean;
      align?: string;
      modelValue?: boolean; // Add this to control open state externally
    }>(),
    {
      dataTest: '',
      center: false,
      modelValue: false,
    },
  );

  const emit = defineEmits<{
    'update:modelValue': [value: boolean];
  }>();

  const open = ref(props.modelValue || false);

  // Watch for external changes and emit internal changes
  watch(
    () => props.modelValue,
    newVal => {
      open.value = newVal;
    },
  );

  watch(open, newVal => {
    emit('update:modelValue', newVal);
  });
</script>

<style lang="scss">
  @import '@/assets/styles/variables';

  // Radix overrides

  .DropdownContent {
    border-radius: 1rem;
    margin: 0.5rem 1rem;
    width: fit-content;
    box-shadow: 0px 4px 15px 0px #0116270d;
    outline: solid 1px #00000004;
    background-color: $white;
    &.center {
      background: none;
      box-shadow: none;
    }

    &:focus {
      outline: none;
    }
  }

  .DropdownMenuTrigger {
    &:focus {
      border-radius: 0.5rem;
      outline: 2px dotted $input-border;
    }
  }
</style>

<style lang="scss" scoped>
  @import '@/assets/styles/variables';
  .DropdownContent {
    padding: 1rem 0;
  }

  .WDropdownMenu {
    .default-trigger {
      display: flex;
      justify-content: center;
      align-items: center;
      border-radius: 100%;
      width: 2rem;
      height: 2rem;
      padding: 0.25rem;
      font-size: 1.25rem;
      color: $neutral-gray;

      &:hover {
        background-color: $bg-light;
      }
    }
  }
</style>
